<template>
    <div class="map-container" ref="mapContainer">
        <!-- Map Loading Animation -->
        <div v-if="isMapLoading" class="map-loading-container">
            <div class="lottie-container">
                <LottieWeb :animationData="lottieMapAnimation" width="200" height="200" :autoplay="true" :loop="true"
                    :show="isMapLoading" />
            </div>
            <p class="loading-text">Loading map data...</p>
        </div>

        <!-- Leaflet Map -->
        <l-map v-else ref="map" :zoom="zoom" :center="center" @ready="onMapReady" style="height: 600px; width: 100%"
            :options="{ zoomControl: true, attributionControl: false }">
            <l-tile-layer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" :detectRetina="true" />

            <!-- Center Marker -->
            <l-marker v-if="centerMarker" :lat-lng="centerMarker.position" :icon="centerMarker.icon">
                <l-popup>
                    <div>Center Location</div>
                </l-popup>
            </l-marker>

            <!-- Service Markers -->
            <l-marker v-for="marker in serviceMarkers" :key="marker.id" :lat-lng="marker.position" :icon="marker.icon">
                <l-popup>
                    <div>
                        <strong>{{ marker.name }}</strong><br>
                        <small>{{ marker.type }}</small>
                    </div>
                </l-popup>
            </l-marker>

            <!-- Transit Markers -->
            <l-marker v-for="marker in transitMarkers" :key="marker.id" :lat-lng="marker.position" :icon="marker.icon">
                <l-popup>
                    <div>
                        <strong>{{ marker.station }}</strong><br>
                        <small>{{ marker.line }} - {{ marker.mode }}</small><br>
                        <small>Next: {{ marker.time }}</small>
                    </div>
                </l-popup>
            </l-marker>

            <!-- Default City Center Marker -->
            <l-marker :lat-lng="center" :icon="defaultMarkerIcon">
                <l-popup>
                    <div>City Center</div>
                </l-popup>
            </l-marker>
        </l-map>
    </div>
</template>

<script setup>
import { ref, watch, nextTick, onBeforeUnmount, computed, defineProps, defineEmits, defineExpose } from 'vue'
import { LMap, LTileLayer, LMarker, LPopup } from '@vue-leaflet/vue-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import LottieWeb from '@/components/general/ChildComponents/LottieWeb.vue'
import lottieMapAnimation from '@/components/general/Commute/commuteAnimations/lottie_map.json'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

// Leaflet icon setup
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
})

// Props
const props = defineProps({
    center: {
        type: Array,
        required: true
    },
    zoom: {
        type: Number,
        default: 13
    },
    isMapLoading: {
        type: Boolean,
        default: true
    },
    serviceMarkers: {
        type: Array,
        default: () => []
    },
    transitMarkers: {
        type: Array,
        default: () => []
    },
    centerMarker: {
        type: Object,
        default: null
    },
    isLoading: {
        type: Boolean,
        default: false
    },
    isLoadingTransport: {
        type: Boolean,
        default: false
    }
})

// Emits
const emit = defineEmits(['map-ready'])

// Refs
const map = ref(null)
const mapContainer = ref(null)

// Default marker icon
const defaultMarkerIcon = L.icon({
    iconUrl: markerIcon,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowUrl: markerShadow,
    shadowSize: [41, 41],
})

// Utility functions
const debounce = (fn, ms) => {
    let timeoutId
    return (...args) => {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(() => fn(...args), ms)
    }
}

// Create HTML icon for services
const createHtmlIcon = (svgUrl, width = 30, height = 30, isCenter = false, name = '') => {
    let iconUrl = svgUrl

    return L.divIcon({
        html: `
            <div style="
                width: ${width}px; 
                height: ${height}px; 
                background-image: url('${iconUrl}');
                background-size: contain;
                background-repeat: no-repeat;
                background-position: center;
                border-radius: ${isCenter ? '50%' : '20%'};
                border: 2px solid ${isCenter ? '#ff6b6b' : '#4ecdc4'};
                background-color: white;
                box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            " title="${name}"></div>
        `,
        className: isCenter ? 'center-marker-html' : 'service-marker-html',
        iconSize: [width, height],
        iconAnchor: [width / 2, height / 2],
        popupAnchor: [0, -height / 2],
    })
}

// Create transit icon
const createTransitIcon = (mode, line) => {
    let color = '#4ecdc4'
    let symbol = '🚊'

    if (line?.startsWith('S')) {
        color = '#009440'
        symbol = 'S'
    } else if (line?.startsWith('U')) {
        color = '#0066AD'
        symbol = 'U'
    } else if (mode === 'bus' || line?.toLowerCase().includes('bus')) {
        color = '#A3195B'
        symbol = '🚌'
    }

    return L.divIcon({
        html: `
            <div style="
                width: 32px; 
                height: 32px; 
                background-color: ${color};
                color: white;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 50%;
                font-weight: bold;
                font-size: 14px;
                border: 3px solid white;
                box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            ">${symbol}</div>
        `,
        className: 'transit-marker',
        iconSize: [32, 32],
        iconAnchor: [16, 16],
        popupAnchor: [0, -16],
    })
}

// Auto-zoom to fit all markers
const autoZoomMap = () => {
    if (!map.value?.leafletObject) return

    const allMarkers = [
        ...(props.centerMarker ? [props.centerMarker.position] : []),
        ...props.serviceMarkers.map(m => m.position),
        ...props.transitMarkers.map(m => m.position),
        props.center,
    ]

    if (allMarkers.length > 1) {
        const bounds = L.latLngBounds(allMarkers)
        map.value.leafletObject.fitBounds(bounds, {
            padding: [50, 50],
            maxZoom: 16,
        })
    } else if (allMarkers.length === 1) {
        map.value.leafletObject.setView(allMarkers[0], 13)
    }
}

// Map ready handler
const onMapReady = () => {
    nextTick(() => {
        if (map.value?.leafletObject) {
            map.value.leafletObject.invalidateSize()
            autoZoomMap()
        }
    })

    const resizeObserver = new ResizeObserver(
        debounce(() => {
            if (map.value?.leafletObject) map.value.leafletObject.invalidateSize()
        }, 100)
    )

    if (mapContainer.value) resizeObserver.observe(mapContainer.value)

    onBeforeUnmount(() => resizeObserver.disconnect())

    emit('map-ready')
}

// Watch for data changes and auto-zoom
watch([() => props.serviceMarkers, () => props.transitMarkers, () => props.centerMarker], () => {
    nextTick(() => {
        if (map.value?.leafletObject && !props.isMapLoading) {
            autoZoomMap()
        }
    })
}, { deep: true })

// Watch for map loading changes
watch(() => props.isMapLoading, (newVal) => {
    if (!newVal) {
        nextTick(() => {
            if (map.value?.leafletObject) {
                map.value.leafletObject.invalidateSize()
                autoZoomMap()
            }
        })
    }
})

// Expose methods to parent
defineExpose({
    autoZoomMap,
    createHtmlIcon,
    createTransitIcon,
    map
})
</script>

<style scoped>
.map-container {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    height: 600px;
    background-color: #ffffff;
    border: 1px solid #e2e8f0;
    position: relative;
}

.map-loading-container {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: #f8fafc;
    z-index: 1000;
}

.lottie-container {
    width: 200px;
    height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.loading-text {
    margin-top: 16px;
    color: #64748b;
    font-size: 14px;
    font-weight: 500;
}

@media (max-width: 960px) {
    .map-container {
        height: 500px;
    }
}

@media (max-width: 600px) {
    .map-container {
        height: 400px;
    }
}

@media (max-width: 480px) {
    .map-container {
        height: 350px;
    }
}
</style>